import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, MotionConfig } from "framer-motion";
import { FONT } from "@/lib/tokens";
import { appleEase } from "@/lib/motion";
import imgEntreprise from "@/assets/images/duo-entreprise.webp";
import imgCreateur from "@/assets/images/duo-createur.webp";

/* ──────────────────────────────────────────────────────────────
   L'aiguillage.

   Toute la moitie basse de l'accueil (Sellers, Asset, Logic,
   Standard, Promo) s'adresse aux CREATEURS : "votre savoir-faire",
   "vendez en continu", "vous percevez un revenu". Rien ne le disait,
   et le visiteur venu pour acheter tombait sans prevenir sur une
   page de recrutement — le reproche des premiers relecteurs
   externes : "on ne comprend pas si c'est pour acheter ou vendre".

   Deux cartes, deux publics nommes. Chacun se reconnait et prend sa
   porte ; personne ne lit la moitie du site qui ne le concerne pas.
   Les deux traitements different volontairement : la photo pleine
   pour l'entreprise (le lieu ou le systeme s'installe), le sujet
   detoure sous le texte pour le createur (la personne, et son
   savoir-faire).
   ────────────────────────────────────────────────────────────── */

const INK  = "#1D1D1F";
const BLUE = "#1D5FD1";

const CSS = `
  .tg-duo-section {
    background: #F5F5F7;
    padding: 110px clamp(24px, 5.5vw, 80px) 118px;
    font-family: ${FONT};
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .tg-duo-title {
    font-size: clamp(34px, 5.4vw, 56px);
    font-weight: 700;
    letter-spacing: -0.035em;
    line-height: 1.1;
    text-align: center;
    max-width: 780px;
    margin: 0 0 56px;
    /* Deux teintes, pas trois : le sable du batiment vers l'encre du
       reste de la page. Fallback en encre pleine si background-clip:
       text n'est pas supporte. */
    color: ${INK};
    background: linear-gradient(96deg, #8A7663 0%, ${INK} 100%);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .tg-duo-grid {
    width: min(1120px, 100%);
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 26px;
    align-items: start;
  }

  .tg-duo-col { display: flex; flex-direction: column; }

  /* Hors mobile les deux cartes sont cote a cote : aucune puce. */
  .tg-duo-dots { display: none; }

  /* L'etiquette de public, au-dessus de la carte : c'est elle qui
     fait tout le travail de clarification. */
  .tg-duo-tag {
    font-size: clamp(17px, 1.7vw, 20px);
    font-weight: 800;
    letter-spacing: 0.05em;
    text-transform: uppercase;
    color: ${INK};
    margin: 0 0 16px 2px;
  }

  .tg-duo-card {
    display: block;
    position: relative;
    height: clamp(400px, 40vw, 500px);
    border-radius: 26px;
    overflow: hidden;
    text-decoration: none;
    box-sizing: border-box;
    transition: transform 0.4s ease, box-shadow 0.4s ease;
  }

  .tg-duo-card:hover { transform: translateY(-4px); }

  /* ── Entreprise : photo pleine, texte pose dessus ── */
  .tg-duo-photo { box-shadow: 0 1px 2px rgba(0,0,0,0.04), 0 18px 40px -22px rgba(0,0,0,0.30); }

  .tg-duo-photo img {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
  }

  /* Sans ce voile, le texte blanc se perd dans le beton clair. */
  .tg-duo-veil {
    position: absolute;
    inset: 0;
    background: linear-gradient(180deg, rgba(0,0,0,0) 34%, rgba(0,0,0,0.62) 100%);
  }

  .tg-duo-photo-text {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    padding: 30px clamp(22px, 2.4vw, 32px);
  }

  .tg-duo-photo-text .tg-duo-q { color: #FFFFFF; }
  .tg-duo-photo-text .tg-duo-cta {
    color: #FFFFFF;
    border-color: rgba(255, 255, 255, 0.9);
  }
  .tg-duo-photo:hover .tg-duo-cta { background: rgba(255, 255, 255, 0.16); }

  /* ── Createur : texte en haut, sujet detoure en dessous ── */
  .tg-duo-figure {
    background: #FFFFFF;
    border: 1px solid ${INK};
    display: flex;
    flex-direction: column;
  }

  .tg-duo-figure-text {
    padding: 30px clamp(22px, 2.4vw, 32px) 0;
    position: relative;
    z-index: 2;
  }

  .tg-duo-figure-text .tg-duo-q { color: ${INK}; }
  .tg-duo-figure-text .tg-duo-cta { color: ${BLUE}; }

  .tg-duo-figure-img {
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: flex-end;
    justify-content: center;
  }

  /* Ancre en bas : le sujet est detoure, il doit se poser sur le
     bord de la carte, pas flotter au milieu. */
  .tg-duo-figure-img img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
    object-position: bottom;
    display: block;
  }

  /* ── Textes communs aux deux cartes ── */
  .tg-duo-q {
    font-size: clamp(21px, 2.1vw, 25px);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.25;
    margin: 0 0 12px;
  }

  /* Pastille a bordure seule : c'est un aiguillage, pas un bouton
     d'achat — il ne doit pas peser plus que la question posee. */
  .tg-duo-cta {
    font-size: 15px;
    font-weight: 600;
    letter-spacing: -0.01em;
    display: inline-block;
    border: 1px solid ${BLUE};
    border-radius: 999px;
    padding: 9px 19px;
    transition: background 0.3s ease;
  }

  .tg-duo-card:hover .tg-duo-cta { background: rgba(29, 95, 209, 0.10); }

  /* ── Mobile : les deux cartes defilent lateralement ───────────
     Le padding lateral passe de la section a la bande, pour que
     celle-ci touche les bords de l'ecran tout en gardant 22 px avant
     la premiere carte : une image collee au bord n'est jamais belle.
     Le spacer final rend la meme marge a droite, le padding-right
     d'un conteneur scrollable etant ignore par certains navigateurs.
     Meme mecanique que la galerie d'exemples, juste au-dessus. */
  @media (max-width: 900px) {
    .tg-duo-section { padding: 72px 0 78px; }
    .tg-duo-title   { font-size: 34px; margin-bottom: 26px; padding: 0 24px; }

    /* Les puces disent qu'il y a autre chose a droite. */
    .tg-duo-dots {
      display: flex;
      justify-content: center;
      gap: 8px;
      margin: 0 0 18px;
    }

    .tg-duo-dot {
      width: 7px;
      height: 7px;
      padding: 0;
      border: none;
      border-radius: 999px;
      background: #C7C7CC;
      cursor: pointer;
      transition: background 0.25s ease, width 0.25s ease;
      -webkit-appearance: none;
      appearance: none;
    }
    .tg-duo-dot.is-active { width: 22px; background: ${INK}; }

    .tg-duo-grid {
      display: flex;
      width: 100%;
      gap: 14px;
      padding: 6px 22px 10px;
      box-sizing: border-box;
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
      scroll-snap-type: x mandatory;
      scroll-padding-left: 22px;
      scrollbar-width: none;
    }
    .tg-duo-grid::-webkit-scrollbar { display: none; }
    .tg-duo-grid::after { content: ""; flex: 0 0 8px; }

    .tg-duo-col {
      flex: 0 0 86%;
      scroll-snap-align: start;
    }

    .tg-duo-card { height: 420px; }
    .tg-duo-q    { font-size: 22px; }
  }
`;

export default function AudienceSplitSection() {
  /* La bande n'existe qu'en mobile ; ailleurs les puces sont masquees
     en CSS et ce code ne coute rien. */
  const gridRef = useRef(null);
  const [active, setActive] = useState(0);

  const syncActive = useCallback(() => {
    const grid = gridRef.current;
    if (!grid) return;
    const left = grid.scrollLeft;
    let best = 0;
    let bestGap = Infinity;
    Array.from(grid.children).forEach((child, i) => {
      if (!(child instanceof HTMLElement)) return;
      const gap = Math.abs(child.offsetLeft - grid.offsetLeft - left);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    setActive(best);
  }, []);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return undefined;
    grid.addEventListener("scroll", syncActive, { passive: true });
    return () => grid.removeEventListener("scroll", syncActive);
  }, [syncActive]);

  const goTo = (i) => {
    const grid = gridRef.current;
    const card = grid?.children?.[i];
    if (!grid || !card) return;
    grid.scrollTo({ left: card.offsetLeft - grid.offsetLeft, behavior: "smooth" });
  };

  const reveal = {
    initial: { opacity: 0, y: 24 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.25 },
    transition: { duration: 0.8, ease: appleEase },
  };

  return (
    <MotionConfig reducedMotion="user">
      <style>{CSS}</style>
      <section className="tg-duo-section">
        <motion.h2 className="tg-duo-title" {...reveal}>
          Deux façons d&apos;entrer dans TANGIG.
        </motion.h2>

        <div className="tg-duo-dots" role="tablist" aria-label="Entreprise ou créateur">
          {["Entreprise", "Créateur"].map((label, i) => (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={i === active}
              aria-label={label}
              className={`tg-duo-dot${i === active ? " is-active" : ""}`}
              onClick={() => goTo(i)}
            />
          ))}
        </div>

        <div className="tg-duo-grid" ref={gridRef}>

          <motion.div className="tg-duo-col" {...reveal}>
            <p className="tg-duo-tag">Entreprise</p>
            <Link to="/marketplace" className="tg-duo-card tg-duo-photo">
              <img src={imgEntreprise} alt="" loading="lazy" />
              <span className="tg-duo-veil" />
              <div className="tg-duo-photo-text">
                <h3 className="tg-duo-q">
                  Vous cherchez un système à installer&nbsp;?
                </h3>
                <span className="tg-duo-cta">Voir les systèmes</span>
              </div>
            </Link>
          </motion.div>

          <motion.div
            className="tg-duo-col"
            {...reveal}
            transition={{ ...reveal.transition, delay: 0.1 }}
          >
            <p className="tg-duo-tag">Créateur</p>
            <Link to="/devenir-createur" className="tg-duo-card tg-duo-figure">
              <div className="tg-duo-figure-text">
                <h3 className="tg-duo-q">
                  Vous avez un savoir-faire à transformer&nbsp;?
                </h3>
                <span className="tg-duo-cta">Devenir créateur</span>
              </div>
              <div className="tg-duo-figure-img">
                <img src={imgCreateur} alt="" loading="lazy" />
              </div>
            </Link>
          </motion.div>

        </div>
      </section>
    </MotionConfig>
  );
}
